/**
 * Kid state — talks to the app's own backend (server/index.js), the durable
 * source of truth (server/child_state.json) and the Hermes proxy. No secret in the browser.
 *
 * Persisted server-side:
 *   - rank         : Child's current rank (child mode only)
 *   - lockoutUntil : epoch ms of the Security Parameter Delta lockout (refresh-proof)
 *   - armed        : operator armed Kid Mode from the console
 *   - mode         : "child" (default) | "unidentified" (a room of unknown children)
 */
import { useCallback, useEffect, useState } from "react";

export type KidMode = "child" | "unidentified";
export type Profile = "kid" | "regular" | "discreet" | "stealth" | "privacy";
export type ChildState = {
  rank: string;
  lockoutUntil: number;
  armed: boolean;
  mode: KidMode;
  profile: Profile;
  correctCount: number;
  triviaLockedUntil: number;
  nextRank: string | null;
  nextThreshold: number | null;
  openingDirective?: string | null;
  lexiconAvailable?: boolean;
  liveDirective?: string | null;
  interruptDirective?: string | null;
  dadMsgAvailable?: boolean;
  dadMsgUrl?: string | null;
  robotArm?: { status: string; lastNote: string; lastTouched: number };
  openActivity?: { id: string; title: string; at: number } | null;
  dossier?: {
    ungated: string[];
    cleared: { rank: string; title: string; fact: string }[];
    owed?: { rank: string; title: string; fact: string }[]; // cleared but not yet ANIMATED
  };
  ladder?: string[];
  // Rank panel (JARVIS-80): earned-rank dates + WHICH ranks hold a dossier file
  // (ranks only — titles/facts for unearned tiers never reach the client).
  promotions?: { rank: string; at: number; reason?: string }[];
  dossierTiers?: string[];
  // Anti-lassitude (chantier D)
  recentActivities?: { type: string; room: string | null }[];
  dailyLegendary?: { id: string; name: string; emoji: string; room: string; owned: boolean } | null;
  surprise?: { label: string; task: string } | null;
  weeklyMystery?: { id: string; title: string; room: string; episodeNum: number; totalEpisodes: number; text: string; isFinal: boolean } | null;
  gradeProgress?: { have: number; span: number; nextRank: string; sub: number; subOf: number } | null;
  todayCorrect?: number;
};

const API_BASE = process.env.REACT_APP_API_BASE ?? "";
const DEFAULT_STATE: ChildState = {
  rank: "Cadet",
  lockoutUntil: 0,
  armed: false,
  mode: "child",
  profile: "kid",
  correctCount: 0,
  triviaLockedUntil: 0,
  nextRank: "Recrue",
  nextThreshold: 3,
  lexiconAvailable: false,
  robotArm: { status: "building", lastNote: "", lastTouched: 0 },
  openActivity: null,
  dossier: { ungated: [], cleared: [], owed: [] },
};

export function useChildState(pollMs = 4000) {
  const [state, setState] = useState<ChildState>(DEFAULT_STATE);
  const [loaded, setLoaded] = useState(false);
  // Operator-APPROVED memories injected into JARVIS's system instruction. Only
  // approved facts are exposed here (the server never leaks the pending queue).
  const [memories, setMemories] = useState<string[]>([]);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/child/state`);
      if (r.ok) setState(await r.json());
    } catch (e) {
      console.error("child/state fetch failed; using local default", e);
    } finally {
      setLoaded(true);
    }
  }, []);

  // Pulled once on mount (and after a promotion refresh) — memories change only
  // when the operator approves one, so no need to poll them every tick.
  const refreshMemories = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/child/memory`);
      if (r.ok) {
        const data = await r.json();
        if (Array.isArray(data.memories)) setMemories(data.memories);
      }
    } catch (e) {
      console.error("child/memory fetch failed; proceeding with none", e);
    }
  }, []);

  // Poll so the tablet picks up console arm/disarm without a manual refresh.
  // Memories change only on operator approval, so re-pull them at a slower cadence
  // (every ~15th tick) rather than every state poll.
  useEffect(() => {
    refresh();
    refreshMemories();
    if (!pollMs) return;
    let ticks = 0;
    const t = setInterval(() => {
      refresh();
      if (++ticks % 15 === 0) refreshMemories();
    }, pollMs);
    return () => clearInterval(t);
  }, [refresh, refreshMemories, pollMs]);

  const promote = useCallback(async (newRank: string, reason?: string) => {
    try {
      const r = await fetch(`${API_BASE}/api/child/promote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_rank: newRank, reason }),
      });
      if (r.ok) setState(await r.json());
    } catch (e) {
      console.error("child/promote failed", e);
    }
  }, []);

  const startLockout = useCallback(async (minutes = 5) => {
    try {
      const r = await fetch(`${API_BASE}/api/child/lockout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ minutes }),
      });
      if (r.ok) setState(await r.json());
    } catch (e) {
      console.error("child/lockout persist failed; enforcing locally", e);
      setState((s) => ({ ...s, lockoutUntil: Date.now() + minutes * 60_000 }));
    }
  }, []);

  return { state, loaded, memories, refresh, refreshMemories, promote, startLockout, API_BASE };
}
